import type { Account, Conversation, ConversationSummary, DisplayMessage, Id, LastMessagePreview, Message, Part, Pin } from '@/model';
import { conversationIdentity, conversationTitle, hasDefaultConversationTitle } from './conversation-names';

export function preview(message: Message | null): LastMessagePreview | null {
  if (!message) return null;
  const base = { senderId: message.sender_id, createdAt: message.created_at };
  if (message.deleted_at) return { ...base, kind: 'deleted', text: null };
  const text = message.parts.find(p => p.type === 'text' && typeof p.text === 'string');
  if (text) return { ...base, kind: 'text', text: String(text.text) };
  const part = message.parts[0];
  const kind = part?.type === 'media' ? part.kind : part?.type;
  return { ...base, kind: ['image', 'video', 'audio', 'file', 'link', 'system'].includes(String(kind)) ? kind as LastMessagePreview['kind'] : 'unsupported', text: null };
}

export function summarize(conversation: Conversation, accountId: Id, accounts: Map<Id, Account>, pins: Pin[], aliases: ReadonlyMap<Id, string> = new Map()): ConversationSummary {
  const identity = conversationIdentity(conversation, accountId, accounts);
  const active = identity.members; const peer = identity.peer;
  const self = conversation.kind === 'self';
  const agent = identity.agentChat || conversation.kind === 'direct' && peer?.kind === 'agent';
  const kind = self ? 'self' : agent ? 'agent' : conversation.kind === 'direct' ? 'direct' : 'group';
  const title = conversationTitle(conversation, accountId, accounts, aliases);
  const personAvatar = conversation.kind === 'direct' || conversation.kind === 'group' && active.length === 2 && hasDefaultConversationTitle(conversation) && !conversation.avatar_media_id ? peer : null;
  const ownPhoto = typeof conversation.avatar_media_id === 'string' ? conversation.avatar_media_id : null;
  const accountPhoto = peer && !peer.deleted ? peer.avatar_media_id : null;
  const avatar = self ? { mediaId: null, seed: conversation.id, name: title, shape: 'person' as const }
    : identity.keepsAgentIdentity ? { mediaId: ownPhoto ?? accountPhoto, seed: ownPhoto ? conversation.id : identity.agentId!, name: title, shape: 'agent' as const }
    : personAvatar ? { mediaId: accountPhoto, seed: personAvatar.id, name: personAvatar.deleted ? '?' : personAvatar.display_name, shape: personAvatar.kind === 'agent' ? 'agent' as const : 'person' as const }
    : { mediaId: ownPhoto ?? (!active.length && typeof conversation.auto_avatar_media_id === 'string' ? conversation.auto_avatar_media_id : null), seed: conversation.id, name: title, shape: 'group' as const };
  const pin = pins.find(p => p.conversation_id === conversation.id);
  return {
    id: conversation.id, conversation, kind, title, peer,
    avatar,
    lastMessage: preview(conversation.last_message), lastActivityAt: conversation.last_message_at ?? conversation.created_at,
    unreadCount: conversation.me.unread_count, firstUnreadSeq: conversation.me.first_unread_seq, notify: conversation.me.notify,
    archived: conversation.me.archived, pinned: pin !== undefined, pinPosition: pin?.position ?? null,
    left: conversation.me.left_seq !== null, memberCount: active.length,
  };
}

export function sortSummaries(rows: ConversationSummary[]): ConversationSummary[] {
  return rows.sort((a, b) => Number(b.pinned) - Number(a.pinned) || (a.pinned && b.pinned ? (a.pinPosition ?? 0) - (b.pinPosition ?? 0) : b.lastActivityAt.localeCompare(a.lastActivityAt)) || a.id.localeCompare(b.id));
}

export function display(message: Message): DisplayMessage {
  return { key: message.client_id || message.id, message, delivery: 'sent' };
}

/** Unknown parts retain their original fields, including future payloads used by registered renderers. */
export function normalizeParts(value: unknown): Part[] {
  if (!Array.isArray(value)) return [];
  return value.map(p => p !== null && typeof p === 'object' && typeof p.type === 'string' ? p as Part : { type: 'unsupported', raw: p });
}
